import React, { useState, useEffect, useCallback, Fragment } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { dashboardToast } from "../../utils/dashboardToast";
import "bootstrap/dist/css/bootstrap.min.css";
import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import SaListPageShell from "../../components/sa/SaListPageShell";
import ContentLoader from "../../components/ContentLoader";
import RecordDetailModal from "../../components/RecordDetailModal";
import CatalogFilterChip from "../../components/sa/CatalogFilterChip";
import TableSortSelect from "../../components/TableSortSelect";
import { Alert, Button as UiButton, Pagination as UiPagination } from "../../components/ui";
import { TABLE_SORT } from "../../utils/tableSort";
import {
  getCapabilityRequestData,
  updateCapabilityRequest,
  type CapabilityApprovalRequest,
} from "../../services/capabilitiesApi";
import { notifySaPendingApprovalsChanged } from "../../utils/saPendingApprovals";
import { Check, ClipboardList, Network, Pencil, Search, User, X } from "lucide-react";
import "../../assets/css/dashboard.css";

const getRequestKey = (request: CapabilityApprovalRequest) =>
  `${request.userName}::${request.projectName}`;

const formatDate = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const EmptyCell = () => <span className="app-td__empty">—</span>;

const getStatusClass = (status: string) => {
  switch (status) {
    case "Approved":
      return "app-status app-status--success";
    case "Rejected":
      return "app-status app-status--danger";
    case "Pending":
      return "app-status app-status--warning";
    default:
      return "app-status app-status--muted";
  }
};

const StatusPill = ({ status }: { status: string }) => (
  <span className={getStatusClass(status)}>{status}</span>
);

const CapabilityApproval = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedRequest, setSelectedRequest] =
    useState<CapabilityApprovalRequest | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewRequest, setViewRequest] =
    useState<CapabilityApprovalRequest | null>(null);
  const [comment, setComment] = useState("");
  const [processingKey, setProcessingKey] = useState<string | null>(null);

  const [approvalRequests, setApprovalRequests] = useState<
    CapabilityApprovalRequest[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState(TABLE_SORT.DATE_DESC);

  const [apiPageInfo, setApiPageInfo] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalPages: 1,
    totalRecords: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
    return item;
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchApprovalRequests = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getCapabilityRequestData(
        debouncedSearchTerm,
        statusFilter,
        apiPageInfo.pageNumber,
        apiPageInfo.pageSize,
        sortKey
      );

      setApprovalRequests(result.data);
      setApiPageInfo((prev) => ({
        ...prev,
        totalRecords: result.totalRecords,
        totalPages: result.totalPages,
        hasNextPage: result.hasNextPage,
        hasPreviousPage: result.hasPreviousPage,
      }));
    } catch (err) {
      console.error("Error fetching approval requests:", err);
      const message =
        err instanceof Error ? err.message : "Failed to fetch approval requests";
      setError(message);
      dashboardToast.error("Failed to fetch approval requests");
    } finally {
      setLoading(false);
    }
  }, [
    debouncedSearchTerm,
    statusFilter,
    apiPageInfo.pageNumber,
    apiPageInfo.pageSize,
    sortKey,
  ]);

  useEffect(() => {
    fetchApprovalRequests();
  }, [fetchApprovalRequests]);

  const openReviewModal = useCallback((request: CapabilityApprovalRequest) => {
    setSelectedRequest(request);
    setComment(request.comment || "");
    setShowReviewModal(true);
  }, []);

  const closeReviewModal = useCallback(() => {
    setShowReviewModal(false);
    setSelectedRequest(null);
    setComment("");
  }, []);

  const openViewModal = useCallback((request: CapabilityApprovalRequest) => {
    setViewRequest(request);
    setShowViewModal(true);
  }, []);

  const closeViewModal = useCallback(() => {
    setShowViewModal(false);
    setViewRequest(null);
  }, []);

  const handleAction = useCallback(
    async (status: "Approved" | "Rejected") => {
      if (!selectedRequest) return;

      if (!comment.trim()) {
        dashboardToast.error("A comment is required before approving or rejecting.");
        return;
      }

      const requestKey = getRequestKey(selectedRequest);
      setProcessingKey(requestKey);

      try {
        await updateCapabilityRequest({
          userName: selectedRequest.userName,
          projectName: selectedRequest.projectName,
          status,
          comment: comment.trim(),
        });

        dashboardToast.success(
          `Request ${status === "Approved" ? "approved" : "rejected"} successfully`
        );
        closeReviewModal();
        fetchApprovalRequests();
        notifySaPendingApprovalsChanged();
      } catch (actionError) {
        console.error(`Error ${status.toLowerCase()}ing request:`, actionError);
        dashboardToast.error(`Failed to ${status.toLowerCase()} request`);
      } finally {
        setProcessingKey(null);
      }
    },
    [selectedRequest, comment, closeReviewModal, fetchApprovalRequests]
  );

  const goToPage = useCallback(
    (n: number) => {
      const pg = Math.min(Math.max(n, 1), apiPageInfo.totalPages);
      if (pg !== apiPageInfo.pageNumber) {
        setApiPageInfo((prev) => ({ ...prev, pageNumber: pg }));
      }
    },
    [apiPageInfo.pageNumber, apiPageInfo.totalPages]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
      setApiPageInfo((prev) => ({ ...prev, pageNumber: 1 }));
    },
    []
  );

  const handleStatusFilterChange = useCallback((nextStatus: string) => {
    setStatusFilter(nextStatus);
    setApiPageInfo((prev) => ({ ...prev, pageNumber: 1 }));
  }, []);

  const handleSortChange = useCallback((nextSort: string) => {
    setSortKey(nextSort);
    setApiPageInfo((prev) => ({ ...prev, pageNumber: 1 }));
  }, []);

  const rowOffset = (apiPageInfo.pageNumber - 1) * apiPageInfo.pageSize;
  const isProcessing =
    selectedRequest != null &&
    processingKey === getRequestKey(selectedRequest);

  return (
    <DashboardShell
      isMenuCollapsed={isMenuCollapsed}
      sidebar={
        <Menu
          onSelect={handleMenuSelection}
          selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />
      }
    >
      <SaListPageShell topBarTitle="Capability Approval">
        <div className="dash-frame-parent1">
          <div className="dash-list-toolbar">
            <div className="dash-list-toolbar__intro">
              <h5 className="dash-list-toolbar__title">Capability Requests</h5>
              <p className="dash-list-toolbar__desc">
                Review capability approval requests by user and project.
              </p>
            </div>
            <div className="dash-list-toolbar__actions">
              <div className="dash-search-field dash-table-search">
                <Search className="dash-search-field__icon" aria-hidden />
                <input
                  type="text"
                  placeholder="Search user or project…"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="dash-search-field__input"
                  disabled={loading}
                />
              </div>
              <CatalogFilterChip
                label="Status"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                disabled={loading}
                options={[
                  { value: "Pending", label: "Pending" },
                  { value: "Approved", label: "Approved" },
                  { value: "Rejected", label: "Rejected" },
                  { value: "Processed", label: "Processed" },
                ]}
              />
              <TableSortSelect variant="chip" value={sortKey} onChange={handleSortChange} disabled={loading} />
            </div>
          </div>

          {loading ? (
            <ContentLoader label="Loading capability requests…" />
          ) : error ? (
            <Alert variant="error">{error}</Alert>
          ) : (
            <Fragment>
              <div className="app-table-wrap app-table-wrap--refined">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>User</th>
                      <th>Project</th>
                      <th>Requested</th>
                      <th>Status</th>
                      <th>Comment</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvalRequests.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center">
                          <span className="app-td__desc">No approval requests found.</span>
                        </td>
                      </tr>
                    ) : (
                      approvalRequests.map((request, index) => (
                        <tr
                          key={getRequestKey(request)}
                          className="rdm-row-clickable"
                          onClick={() => openViewModal(request)}
                        >
                          <td>
                            <span className="app-td__index">{rowOffset + index + 1}</span>
                          </td>
                          <td>
                            <span className="app-td__title">{request.userName}</span>
                          </td>
                          <td>
                            <span className="app-td__desc">{request.projectName}</span>
                          </td>
                          <td>
                            <span className="app-td__meta">{formatDate(request.requestDate)}</span>
                          </td>
                          <td>
                            <StatusPill status={request.status} />
                          </td>
                          <td>
                            {request.comment ? (
                              <span className="app-td__desc" title={request.comment}>
                                {request.comment.length > 50
                                  ? `${request.comment.substring(0, 50)}…`
                                  : request.comment}
                              </span>
                            ) : (
                              <EmptyCell />
                            )}
                          </td>
                          <td
                            className="dash-mobile-table-actions"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="btn-list-actions">
                              <UiButton
                                variant="outline"
                                size="sm"
                                className="btn-list-edit"
                                onClick={() => openReviewModal(request)}
                                title={
                                  request.status === "Pending"
                                    ? "Review request"
                                    : "View request details"
                                }
                                aria-label={
                                  request.status === "Pending"
                                    ? `Review ${request.userName}`
                                    : `View ${request.userName}`
                                }
                              >
                                <Pencil />
                              </UiButton>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <UiPagination
                page={apiPageInfo.pageNumber}
                totalPages={apiPageInfo.totalPages}
                onPageChange={goToPage}
              />
            </Fragment>
          )}
        </div>
      </SaListPageShell>

      <Modal
        show={showReviewModal}
        onHide={closeReviewModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedRequest?.status === "Pending"
              ? "Review Capability Request"
              : "Capability Request Details"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <>
              <Row className="g-3 mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      <User className="me-2" />
                      User Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={selectedRequest.userName}
                      readOnly
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      <Network className="me-2" />
                      Project Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={selectedRequest.projectName}
                      readOnly
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <div>
                      <StatusPill status={selectedRequest.status} />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Requested</Form.Label>
                    <Form.Control
                      type="text"
                      value={formatDate(selectedRequest.requestDate)}
                      readOnly
                    />
                  </Form.Group>
                </Col>
                {selectedRequest.approvedDate ? (
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Decision Date</Form.Label>
                      <Form.Control
                        type="text"
                        value={formatDate(selectedRequest.approvedDate)}
                        readOnly
                      />
                    </Form.Group>
                  </Col>
                ) : null}
              </Row>

              <Form.Group className="mb-3">
                <Form.Label className={selectedRequest.status === "Pending" ? "required" : undefined}>
                  <ClipboardList className="me-2" />
                  {selectedRequest.status === "Pending"
                    ? "Review Comment"
                    : "Comment"}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={
                    selectedRequest.status === "Pending"
                      ? "Enter your review comment..."
                      : ""
                  }
                  maxLength={500}
                  readOnly={selectedRequest.status !== "Pending"}
                />
                {selectedRequest.status === "Pending" ? (
                  <Form.Text className="text-muted">
                    {comment.length}/500 characters
                  </Form.Text>
                ) : null}
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeReviewModal}
            style={{ width: "auto" }}
          >
            Close
          </Button>
          {selectedRequest?.status === "Pending" && (
            <>
              <Button
                variant="danger"
                onClick={() => handleAction("Rejected")}
                disabled={isProcessing || !comment.trim()}
                style={{ width: "auto" }}
              >
                <X className="me-1" />
                Reject
              </Button>
              <Button
                className="btn btn-dashboard-primary"
                onClick={() => handleAction("Approved")}
                disabled={isProcessing || !comment.trim()}
                style={{ width: "auto" }}
              >
                <Check className="me-1" />
                Approve
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <RecordDetailModal
        show={showViewModal}
        onHide={closeViewModal}
        title={viewRequest?.projectName || "Capability Request"}
        subtitle={viewRequest?.userName || "Capability approval request"}
        fields={[
          { label: "User Name", value: viewRequest?.userName },
          { label: "Project Name", value: viewRequest?.projectName },
          {
            label: "Requested",
            value: formatDate(viewRequest?.requestDate),
          },
          {
            label: "Status",
            value: viewRequest ? <StatusPill status={viewRequest.status} /> : undefined,
          },
          {
            label: "Comment",
            value: viewRequest?.comment,
            fullWidth: true,
          },
          ...(viewRequest?.approvedDate
            ? [
                {
                  label: "Decision Date",
                  value: formatDate(viewRequest.approvedDate),
                },
              ]
            : []),
        ]}
        actions={[
          {
            type: "review",
            onClick: () => {
              const request = viewRequest;
              closeViewModal();
              if (request) openReviewModal(request);
            },
          },
        ]}
      />

    </DashboardShell>
  );
};

export default CapabilityApproval;
